import { downloadFile, downloadGroupFile } from '@front-app/services/GroupsApi/GroupFileApi';
import { DocumentType } from '@front-app/services/ImageApi/ImageModel';
import { Ref, ref } from '@vue/composition-api';

interface UseFileDownload {
	download(contentSource: string | undefined): Promise<void>;
	isLoading: Ref<boolean>;
}

export const useFileDownload = (): UseFileDownload => {
	const isLoading = ref(false);
	const download = async (contentSource: string | undefined) => {
		if (!contentSource) {
			return;
		}
		isLoading.value = true;
		const file = await downloadFile(contentSource);
		const parsedProps = parseContentSource(contentSource);
		triggerFileSave(file, parsedProps.fn);
		isLoading.value = false;
	};

	return {
		download,
		isLoading,
	};
};

interface UseFileDownloadV2 {
	download(groupId: string, contentId: string, fileName: string): Promise<void>;
	isLoading: Ref<boolean>;
}

export const useFileDownloadV2 = (): UseFileDownloadV2 => {
	const isLoading = ref(false);
	const download = async (groupId: string, contentId: string, fileName: string) => {
		isLoading.value = true;
		const file = await downloadGroupFile(groupId, contentId);
		triggerFileSave(file, fileName);
		isLoading.value = false;
	};

	return {
		download,
		isLoading,
	};
};

interface ContentSource {
	pt: DocumentType;
	md: string;
	sd: string;
	fn: string;
}

function parseContentSource(contentSource: string): ContentSource {
	const values = new URLSearchParams(contentSource);
	return {
		pt: values.get('pt') as DocumentType,
		md: values.get('md') || '',
		sd: values.get('sd') || '',
		fn: values.get('fn') || '',
	};
}

function triggerFileSave(blob: Blob, fileName: string): void {
	// IE doesn't allow using a blob object directly as link href
	// instead it is necessary to use msSaveOrOpenBlob
	if (window.navigator && isIeNavigator(window.navigator)) {
		window.navigator.msSaveOrOpenBlob(blob);
		return;
	}

	// For other browsers:
	// Create a link pointing to the ObjectURL containing the blob.
	const data = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = data;
	link.download = fileName;
	link.click();
	setTimeout(() => {
		// For Firefox it is necessary to delay revoking the ObjectURL
		window.URL.revokeObjectURL(data);
	}, 100);
}

interface IeNavigator {
	msSaveOrOpenBlob(blob: Blob): void;
}

export function isIeNavigator(item: Navigator | IeNavigator): item is IeNavigator {
	return typeof (item as IeNavigator).msSaveOrOpenBlob === 'function';
}
